import type { Tuning } from '../config/tuning'
import type { BossDef, Entity } from '../ecs/entity'
import { makeEntity } from '../ecs/entity'
import { tileCenter } from './templates'

/**
 * Bosses are DATA on the framework: phases, attack lists, add-spawning, and
 * numeric params. Boss One (the Floor Warden) is the reference build.
 *
 * DECISION: the spec makes the campaign beatable with a boss every 5th floor
 * but scopes "boss two/three" out of v1. Resolved by shipping the later boss
 * gates as data-only variants of the framework (no bespoke code): each adds
 * exactly one twist expressed through existing hooks — the Overseer adds the
 * radial 'slam' (arena-hazard pressure), the Director adds a third phase and
 * dual add-types. This keeps v1 honest while proving the framework
 * generalizes, and the campaign climax exists.
 */
export function bossDefs(t: Tuning): Record<string, BossDef> {
  const b = t.boss
  return {
    warden: {
      id: 'warden',
      name: 'FLOOR WARDEN',
      hp: b.baseHp,
      speed: 55,
      radius: b.radius,
      renderId: 'boss_warden',
      params: { chargeSpeed: b.chargeSpeed, telegraph: b.telegraphTime, scale: 1 },
      phases: [
        { threshold: 1.0, attacks: ['charge'], attackInterval: 2.6 },
        {
          threshold: 0.5,
          attacks: ['charge', 'charge'],
          attackInterval: 1.9,
          addSpawning: { type: 'husk', interval: 3.2, aliveCap: 4 }
        }
      ]
    },
    overseer: {
      id: 'overseer',
      name: 'SECTOR OVERSEER',
      hp: b.baseHp + b.hpPerAct,
      speed: 62,
      radius: b.radius + 3,
      renderId: 'boss_overseer',
      params: { chargeSpeed: b.chargeSpeed * 1.05, telegraph: b.telegraphTime * 0.95, scale: 1.12, slamRadius: 130, slamDamage: 26 },
      phases: [
        { threshold: 1.0, attacks: ['charge', 'slam'], attackInterval: 2.4 },
        {
          threshold: 0.5,
          attacks: ['slam', 'charge', 'charge'],
          attackInterval: 1.8,
          addSpawning: { type: 'static', interval: 4.5, aliveCap: 3 }
        }
      ]
    },
    director: {
      id: 'director',
      name: 'BROADCAST DIRECTOR',
      hp: b.baseHp + b.hpPerAct * 2,
      speed: 70,
      radius: b.radius + 5,
      renderId: 'boss_director',
      params: { chargeSpeed: b.chargeSpeed * 1.15, telegraph: b.telegraphTime * 0.9, scale: 1.25, slamRadius: 150, slamDamage: 30 },
      phases: [
        { threshold: 1.0, attacks: ['charge', 'slam'], attackInterval: 2.2 },
        {
          threshold: 0.66,
          attacks: ['charge', 'slam', 'charge'],
          attackInterval: 1.7,
          addSpawning: { type: 'husk', interval: 3.0, aliveCap: 4 }
        },
        {
          threshold: 0.33,
          attacks: ['slam', 'charge', 'charge'],
          attackInterval: 1.4,
          addSpawning: { type: 'foreman', interval: 9.0, aliveCap: 1 }
        }
      ]
    }
  }
}

export function makeBoss(t: Tuning, id: string, tx: number, ty: number): Entity {
  const def = bossDefs(t)[id]
  if (!def) throw new Error(`unknown boss '${id}'`)
  const e = makeEntity('boss', tileCenter(tx), tileCenter(ty), def.radius, def.renderId)
  e.faction = 'network'
  e.hp = def.hp
  e.maxHp = def.hp
  e.boss = {
    def,
    phase: 0,
    stage: 'idle',
    stageT: 0,
    attackIndex: 0,
    currentAttack: '',
    ax: 1,
    ay: 0,
    addT: 0,
    touchT: 0
  }
  return e
}
